import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getEspnLeagueId, getEspnSeason, getEspnTeamId, getSleeperLeagueId, getSleeperUsername } from '../services/leagueStorage.js';
import { connectEspnLeague, syncDraftFromEspn } from '../services/espnDraftSync.js';
import {
  fetchLeagueDrafts,
  formatDraftLabel,
  pickPreferredDraft,
  syncDraftFromSleeper,
} from '../services/sleeperDraftSync.js';
import {
  BOARD_COLUMNS,
  computePositionScarcity,
  DRAFT_POSITIONS,
  fetchLeagueMeta,
  fetchSleeperTeams,
  fetchSleeperUserId,
  filterBoard,
  loadRosterBoardRows,
  nextSortState,
  nextTierCliff,
  sortBoardRows,
} from '../services/draftBoard.js';
import {
  assignModelTiers,
  getModelValuesCached,
  scaledEdge,
} from '../services/modelValues.js';
import { buildConsensusBoard } from '../services/consensusBoard.js';
import { getEspnProjectionsCached, mergeMarketAuction } from '../services/espnProjections.js';
import {
  ADP_GAP_NOISE,
  formatEcrTooltip,
  formatGapTooltip,
  gapLevel,
  getEcrRanksCached,
  mergeEcrRanks,
  withAdpGap,
} from '../services/rankComparison.js';
import { formatAdpSlot, getAdpCached, mergeAdp } from '../services/adp.js';
import {
  formatLastFinish,
  formatLastFinishTooltip,
  formatOffenseTooltip,
  getPlayerContextCached,
  mergePlayerContext,
  OFFENSE_STRONG,
  OFFENSE_WEAK,
} from '../services/playerContext.js';
import {
  DEFAULT_BUDGET,
  formatAuctionTooltip,
  withAuctionValues,
} from '../services/auctionValues.js';
import {
  auctionInflation,
  formatInflationTooltip,
  formatPositionHeat,
  formatPositionTooltip,
  formatRowInflationNote,
  inflatedPrice,
  positionOutliers,
  positionRate,
  RATE_NOISE,
  teamBudgets,
} from '../services/auctionInflation.js';
import { SCORING_LABELS, scoringKeyForPpr } from '../services/juiceSheets.js';
import { loadHeadshotMap, resolveSleeperId } from '../services/playerImages.js';
import PlayerAvatar from './PlayerAvatar.jsx';

/**
 * A board row's face.
 *
 * The board is the one view whose rows have no Sleeper id of their own, so every
 * avatar on it goes through the name-keyed map. `pending` keeps the ring empty
 * until the map lands rather than painting a team logo and flipping it.
 */
const BoardAvatar = ({ row, map, size = 'sm', className = '' }) => (
  <PlayerAvatar
    size={size}
    className={className}
    name={row.name}
    team={row.team}
    position={row.position}
    sleeperId={resolveSleeperId(row, map)}
    pending={map === null}
  />
);
import {
  computeLineupNeeds,
  describeDraft,
  describeEspnDraft,
  draftClock,
  formatCountdown,
  formatPickSlot,
  parseRosterPositions,
  recommendPicks,
  replacementPoints,
  startingPool,
  withWaitCost,
  withAvailabilityOdds,
} from '../services/draftClock.js';

const STORAGE_KEY = 'fantasy_focus_draft';

const loadStored = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const initialFocusMode = () => loadStored().focusMode !== false;

const saveStored = (data) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
};

/**
 * The lineup to assume before a league is connected.
 *
 * It must not be empty: with no starting slots every positional baseline
 * collapses onto the best player at that position, so value-over-replacement
 * reads +0 for all of them and the board fills with quarterbacks. Standard
 * half-PPR 1QB is the most common redraft shape and a safe stand-in.
 */
const DEFAULT_ROSTER_SHAPE = {
  starterSlots: ['QB', 'RB', 'RB', 'WR', 'WR', 'TE', 'FLEX'],
  bench: 6,
  format: 'oneqb',
  startersCount: 7,
};

const SLOT_LABELS = {
  DEF: 'DST',
  SUPER_FLEX: 'SFLX',
  WRRB_FLEX: 'W/R',
  REC_FLEX: 'W/T',
  WRRB_WRT: 'FLEX',
};

const slotLabel = (slot) => SLOT_LABELS[slot] || slot;

const parseRankBy = (stored) => {
  if (stored.rankBy === 'adp' || stored.rankBy === 'value' || stored.rankBy === 'ecr') return stored.rankBy;
  return 'ecr';
};

const parseSort = (stored) =>
  stored.sortKey
    ? { key: stored.sortKey, dir: stored.sortDir === 'desc' ? 'desc' : 'asc' }
    : { key: 'ecr', dir: 'asc' };

/**
 * Redraft draft assistant: live board, tiers, scarcity, roster needs, watchlist,
 * Sleeper sync.
 *
 * The board runs on two numbers and no others: what our own model projects, and
 * what the room is paying (ADP). KeepTradeCut used to be the spine here — it is
 * a trade market, which is a useful thing and still what the Dynasty view is
 * built on, but on draft day it answers a question nobody asked. The platform
 * rank columns went the same way: three sites' opinions of the same players are
 * noise once you have a projection you trust.
 *
 * On draft day the job is to answer three questions fast — who is on the clock,
 * who is worth taking now, and who will still be there when it comes back
 * around — so the tool boots straight into the saved league; live sync is
 * opt-in so a refresh does not overwrite a manual or mock board.
 */
const DraftTool = () => {
  const stored = loadStored();

  const [format, setFormat] = useState(stored.format === 'sf' ? 'sf' : 'oneqb');
  const [formatLocked, setFormatLocked] = useState(Boolean(stored.formatLocked));
  const [positionFilter, setPositionFilter] = useState(stored.positionFilter || 'ALL');
  const [rankBy, setRankBy] = useState(parseRankBy(stored));
  const [sort, setSort] = useState(parseSort(stored));
  const [search, setSearch] = useState('');
  const [draftedIds, setDraftedIds] = useState(() => new Set(stored.draftedIds || []));
  const [draftLog, setDraftLog] = useState(stored.draftLog || []);
  const [watchlist, setWatchlist] = useState(() => new Set(stored.watchlist || []));
  const [loadingBoard, setLoadingBoard] = useState(true);
  const [boardError, setBoardError] = useState('');
  const [modelValues, setModelValues] = useState(null);
  const [espnPlayers, setEspnPlayers] = useState(null);
  const [ecrPlayers, setEcrPlayers] = useState(null);
  const [adpPayload, setAdpPayload] = useState(null);
  const [contextPayload, setContextPayload] = useState(null);
  const [headshotMap, setHeadshotMap] = useState(null);

  const [draftPlatform, setDraftPlatform] = useState(stored.draftPlatform === 'espn' ? 'espn' : 'sleeper');
  const [sleeperLeagueIdInput, setSleeperLeagueIdInput] = useState(
    stored.sleeperLeagueId || (stored.draftPlatform !== 'espn' ? stored.leagueId : '') || getSleeperLeagueId(),
  );
  const [espnLeagueIdInput, setEspnLeagueIdInput] = useState(
    stored.espnLeagueId || (stored.draftPlatform === 'espn' ? stored.leagueId : '') || getEspnLeagueId(),
  );
  const [espnSeasonInput, setEspnSeasonInput] = useState(stored.espnSeason || getEspnSeason());
  const leagueIdInput = draftPlatform === 'espn' ? espnLeagueIdInput : sleeperLeagueIdInput;
  const setLeagueIdInput = draftPlatform === 'espn' ? setEspnLeagueIdInput : setSleeperLeagueIdInput;
  const [usernameInput, setUsernameInput] = useState(stored.username || getSleeperUsername());
  const [leagueMeta, setLeagueMeta] = useState(null);
  const [myUserId, setMyUserId] = useState(stored.myUserId || '');
  const [teams, setTeams] = useState([]);
  const [rosterId, setRosterId] = useState(
    stored.rosterId || (stored.draftPlatform === 'espn' ? getEspnTeamId() : ''),
  );
  const [keeperRows, setKeeperRows] = useState([]);
  const [loadingLeague, setLoadingLeague] = useState(false);
  const [error, setError] = useState('');

  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(stored.draftId || '');
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [rawDraft, setRawDraft] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [unmatchedPicks, setUnmatchedPicks] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [focusMode, setFocusMode] = useState(initialFocusMode);

  const [now, setNow] = useState(() => Date.now());
  const bootedRef = useRef(false);
  const platformRef = useRef(draftPlatform);

  useEffect(() => {
    if (window.location.hash === '#draft') {
      document.getElementById('draft-tool')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Focus mode is a page-level concern (it hides the other panels and widens
  // the grid), so it rides on the body rather than this subtree. useLayoutEffect
  // keeps the class in sync before paint so the sidebar does not flash in.
  useLayoutEffect(() => {
    document.body.classList.toggle('is-draft-focus', focusMode);
    return () => document.body.classList.remove('is-draft-focus');
  }, [focusMode]);

  // A minute hand for the pre-draft countdown.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 20000);
    return () => window.clearInterval(id);
  }, []);

  /* ---------------- board ---------------- */

  const scoringKey = useMemo(
    () => (leagueMeta ? scoringKeyForPpr(leagueMeta.ppr) : 'half'),
    [leagueMeta],
  );

  useEffect(() => {
    let cancelled = false;
    setBoardError('');
    if (!modelValues) setLoadingBoard(true);

    // Our own feed is the whole board now: it carries every drafted position,
    // with kickers, defenses and rookies arriving on ADP alone because the model
    // needs a prior NFL season to project anyone. Format only affects how rows
    // are ranked once league scoring is known — it does not change the fetch.
    getModelValuesCached()
      .then((model) => {
        if (cancelled) return;
        setModelValues(model);
      })
      .catch((err) => {
        if (cancelled) return;
        setBoardError(err.message || 'Could not load redraft rankings.');
      })
      .finally(() => {
        if (!cancelled) setLoadingBoard(false);
      });

    // Every other feed is a column, not the board, and each one fails on its
    // own: a dead ADP fetch costs the ADP and vs-ADP columns and nothing else.
    // The exception is the consensus, which is the board's spine — losing it
    // drops the board back to the model export, which is a shorter list but
    // still a board.
    getEcrRanksCached()
      .then((payload) => {
        if (!cancelled) setEcrPlayers(payload?.players || []);
      })
      .catch(() => {
        if (!cancelled) setEcrPlayers([]);
      });

    getAdpCached()
      .then((payload) => {
        if (!cancelled) setAdpPayload(payload || { players: [] });
      })
      .catch(() => {
        if (!cancelled) setAdpPayload({ players: [] });
      });

    getPlayerContextCached()
      .then((payload) => {
        if (!cancelled) setContextPayload(payload || { players: [], teams: [] });
      })
      .catch(() => {
        if (!cancelled) setContextPayload({ players: [], teams: [] });
      });

    // Faces. The board's rows are keyed by name and position, so they need the
    // build-time map to reach a Sleeper id; `loadHeadshotMap` resolves to an
    // empty object rather than rejecting, so this cannot fail the board.
    loadHeadshotMap().then((map) => {
      if (!cancelled) setHeadshotMap(map);
    });

    // ESPN is here for one thing now: its published auction value, as a market
    // check on the dollars we derive ourselves.
    getEspnProjectionsCached()
      .then((payload) => {
        if (!cancelled) setEspnPlayers(payload?.players || []);
      })
      .catch(() => {
        if (!cancelled) setEspnPlayers([]);
      });

    return () => {
      cancelled = true;
    };
    // Load rankings once on mount; league format is applied via leagueMeta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Positional rank and the gap to the market, both read off our own ordering.
   *
   * `edge` is what makes a board that isn't ADP worth looking at: it is how far
   * we rank a player above where the room is taking him.
   */
  const withPositionalRanks = (rows) => {
    const seen = {};
    return rows.map((row, index) => {
      seen[row.position] = (seen[row.position] || 0) + 1;
      const boardRank = index + 1;
      return {
        ...row,
        posRank: seen[row.position],
        edge: scaledEdge(row.adpRank, boardRank, row.confidence, row.flags),
      };
    });
  };

  /**
   * The board, assembled from five feeds that each fail on their own.
   *
   * Order matters. The consensus decides who is on the board; ADP has to land
   * before the gap is computed, because the gap re-ranks both lists over the
   * rows that carry both numbers; the context and auction merges are additive
   * and could go anywhere.
   */
  const board = useMemo(() => {
    if (!modelValues && !ecrPlayers?.length) return [];

    let rows = buildConsensusBoard({
      ecrPlayers,
      modelPayload: modelValues,
      scoringSettings: leagueMeta?.scoringSettings,
    });

    if (ecrPlayers?.length) rows = mergeEcrRanks(rows, ecrPlayers).board;
    if (adpPayload) rows = mergeAdp(rows, adpPayload).board;
    if (contextPayload) rows = mergePlayerContext(rows, contextPayload).board;
    if (espnPlayers?.length) rows = mergeMarketAuction(rows, espnPlayers).board;

    return withAdpGap(rows);
  }, [modelValues, espnPlayers, ecrPlayers, adpPayload, contextPayload, leagueMeta]);

  const priorSeason = contextPayload?.priorSeason ?? null;

  /* ---------------- league / draft setup ---------------- */

  const connectLeague = useCallback(
    async ({ leagueId, username, season, silent = false } = {}) => {
      const lid = (leagueId ?? leagueIdInput).trim();
      if (!lid) {
        setError(draftPlatform === 'espn' ? 'Enter your ESPN league ID.' : 'Enter your Sleeper league ID.');
        return null;
      }

      if (!silent) setError('');
      setLoadingLeague(true);

      try {
        if (draftPlatform === 'espn') {
          const yr = (season ?? espnSeasonInput).trim();
          if (!yr) {
            setError('Enter the ESPN season year (e.g. 2025).');
            return null;
          }

          const { league, meta, teams: teamList } = await connectEspnLeague({
            leagueId: lid,
            season: yr,
            credentials: 'include',
          });

          setLeagueMeta(meta);
          setTeams(teamList);
          setDrafts([]);
          setMyUserId('');
          setRawDraft(league);

          if (!formatLocked) {
            const shape = parseRosterPositions(meta.rosterPositions);
            setFormat(shape.format);
          }

          const savedTeamId = getEspnTeamId();
          const stillValidTeam = teamList.some((t) => String(t.rosterId) === String(rosterId));
          if (savedTeamId && teamList.some((t) => String(t.rosterId) === savedTeamId)) {
            setRosterId(savedTeamId);
          } else if (!stillValidTeam && teamList.length) {
            setRosterId(String(teamList[0].rosterId));
          }

          return { meta, teamList };
        }

        const handle = (username ?? usernameInput).trim();
        const [meta, teamList, draftList, userId] = await Promise.all([
          fetchLeagueMeta(lid),
          fetchSleeperTeams(lid),
          fetchLeagueDrafts(lid).catch(() => []),
          fetchSleeperUserId(handle).catch(() => ''),
        ]);

        setLeagueMeta(meta);
        setTeams(teamList);
        setDrafts(draftList);
        setMyUserId(userId);

        if (!formatLocked) {
          const shape = parseRosterPositions(meta.rosterPositions);
          setFormat(shape.format);
        }

        const mine =
          (userId && teamList.find((t) => String(t.ownerId) === String(userId))) ||
          (handle &&
            teamList.find(
              (t) =>
                t.username?.toLowerCase() === handle.toLowerCase() ||
                t.displayName?.toLowerCase() === handle.toLowerCase(),
            )) ||
          null;
        const stillValidTeam = teamList.some((t) => String(t.rosterId) === String(rosterId));
        if (mine) setRosterId(String(mine.rosterId));
        else if (!stillValidTeam && teamList.length) setRosterId(String(teamList[0].rosterId));

        if (draftList.length) {
          const stillValidDraft = draftList.some((d) => d.draft_id === draftId);
          const preferred = pickPreferredDraft(draftList);
          setDraftId(stillValidDraft ? draftId : preferred?.draft_id || draftList[0].draft_id);
        }

        return { meta, teamList, draftList, userId };
      } catch (err) {
        setError(err.message || 'Could not load this league.');
        return null;
      } finally {
        setLoadingLeague(false);
      }
    },
    [draftPlatform, leagueIdInput, espnSeasonInput, usernameInput, formatLocked, rosterId, draftId],
  );

  // Boot straight into the saved (or default) league so the tool is useful the
  // moment the page opens — no clicking through setup while the draft starts.
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    if (!leagueIdInput.trim()) return;
    connectLeague({ silent: true });
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (platformRef.current === draftPlatform) return;
    platformRef.current = draftPlatform;
    if (!leagueIdInput.trim()) return;
    connectLeague({ silent: true });
  }, [draftPlatform, leagueIdInput, connectLeague]);

  /* ---------------- live sync ---------------- */

  useEffect(() => {
    if (!syncEnabled || !board.length) return undefined;
    if (draftPlatform === 'sleeper' && !draftId) return undefined;
    if (draftPlatform === 'espn' && !leagueIdInput.trim()) return undefined;

    let cancelled = false;
    let timeoutId;

    const runSync = async () => {
      try {
        const result =
          draftPlatform === 'espn'
            ? await syncDraftFromEspn({
                leagueId: leagueIdInput,
                season: espnSeasonInput,
                board,
                teams,
                credentials: 'include',
              })
            : await syncDraftFromSleeper({ draftId, board, teams });
        if (cancelled) return;

        setRawDraft(result.draft);
        setDraftedIds(new Set(result.draftedIds));
        setDraftLog(result.draftLog);
        setSyncStatus({
          status: result.status,
          pickCount: result.pickCount,
          totalPicks: result.totalPicks,
          draftName: result.draftName,
        });
        setUnmatchedPicks(result.unmatched);
        setLastSyncAt(new Date());
        setSyncError('');

        timeoutId = window.setTimeout(runSync, result.pollMs);
      } catch (err) {
        if (cancelled) return;
        setSyncError(
          err.message ||
            (draftPlatform === 'espn' ? 'ESPN sync failed.' : 'Sleeper sync failed.'),
        );
        timeoutId = window.setTimeout(runSync, 10000);
      }
    };

    runSync();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [syncEnabled, draftPlatform, draftId, leagueIdInput, espnSeasonInput, board, teams]);

  /* ---------------- persistence ---------------- */

  useEffect(() => {
    saveStored({
      draftPlatform,
      format,
      formatLocked,
      positionFilter,
      rankBy,
      sortKey: sort.key,
      sortDir: sort.dir,
      focusMode,
      draftedIds: [...draftedIds],
      draftLog,
      watchlist: [...watchlist],
      leagueId: leagueIdInput.trim(),
      sleeperLeagueId: sleeperLeagueIdInput.trim(),
      espnLeagueId: espnLeagueIdInput.trim(),
      espnSeason: espnSeasonInput.trim(),
      username: usernameInput.trim(),
      myUserId,
      rosterId,
      draftId,
    });
  }, [
    draftPlatform,
    format,
    formatLocked,
    positionFilter,
    rankBy,
    sort,
    focusMode,
    draftedIds,
    draftLog,
    watchlist,
    leagueIdInput,
    sleeperLeagueIdInput,
    espnLeagueIdInput,
    espnSeasonInput,
    usernameInput,
    myUserId,
    rosterId,
    draftId,
  ]);

  /* ---------------- derived draft state ---------------- */

  // Projections, bye weeks, and the three platforms' positional ranks, folded
  // onto the KTC rows they belong to.
  const rosterShape = useMemo(
    () => {
      const shape = leagueMeta ? parseRosterPositions(leagueMeta.rosterPositions) : null;
      return shape?.starterSlots?.length ? shape : DEFAULT_ROSTER_SHAPE;
    },
    [leagueMeta],
  );

  const draftInfo = useMemo(() => {
    if (!rawDraft) return null;
    if (draftPlatform === 'espn') {
      return describeEspnDraft(rawDraft, { teams, myTeamId: rosterId });
    }
    return describeDraft(rawDraft, { teams, userId: myUserId });
  }, [rawDraft, teams, myUserId, rosterId, draftPlatform]);

  /**
   * The auction budget the dollar column is scaled to.
   *
   * A connected auction draft says what it is; anything else gets the standard
   * $200, which is a stated assumption rather than a guess — the column's
   * tooltip names the budget it priced against.
   */
  const auctionBudget = draftInfo?.budget || DEFAULT_BUDGET;

  /**
   * An auction is a different game, not a different setting.
   *
   * There is no draft order, so "who is on the clock" and "how many picks
   * until your turn" have no answer; every player is available to you at every
   * moment and the only constraint is money. The strip and the comparison
   * column both key off this.
   */
  const isAuction = draftInfo?.type === 'auction';

  const rosterSize = rosterShape.starterSlots.length + (rosterShape.bench || 0);

  const projectionsAvailable = board.some((row) => row.proj != null);
  const ecrAvailable = board.some((row) => row.ecrRank != null);
  const rankControlsAvailable = projectionsAvailable || ecrAvailable;

  const rankingBy = (() => {
    if (rankBy === 'ecr') return ecrAvailable ? 'ecr' : 'adp';
    if (rankBy === 'adp') return 'adp';
    return projectionsAvailable ? 'value' : ecrAvailable ? 'ecr' : 'adp';
  })();

  /**
   * Order the board.
   *
   * Value mode ranks by points over replacement, never by raw projected points:
   * raw points would bury every running back under a wall of quarterbacks,
   * since a QB1 outscores an RB1 while a dozen other quarterbacks score nearly
   * as much. The baseline comes from this league's own starting lineup, so a
   * superflex or TE-premium league reprices positions the way it should.
   *
   * Kickers, defenses and rookies have no projection and no place on that
   * scale, so they keep their ADP order behind everyone who does rather than
   * being mixed in at zero.
   */
  const rankedBoard = useMemo(() => {
    if (!board.length) return [];

    const teamCount = draftInfo?.teams || 12;

    const baselines = startingPool(board, rosterShape, teamCount, (row) => row.usageExpected ?? row.expected);

    const withValue = [];
    const withoutValue = [];
    for (const row of board) {
      const baseline = baselines[row.position];
      const valueBase = row.usageExpected ?? row.expected;
      if (valueBase != null && baseline != null) {
        const vor = Math.round((valueBase - baseline) * 10) / 10;
        withValue.push({ ...row, vor, rankValue: vor });
      } else {
        withoutValue.push({ ...row, vor: null, rankValue: null });
      }
    }

    // Dollars are priced off the whole board at once, not row by row: an
    // auction budget is zero-sum, so what one player is worth depends on
    // everyone else who will be bought.
    const priced = withAuctionValues([...withValue, ...withoutValue], {
      budget: auctionBudget,
      teams: teamCount,
      rosterSize,
    });
    // Both numbers travel: the price, and ESPN's own price rebased onto this
    // league's budget so the two can be subtracted.
    const pricedById = new Map(
      priced.map((row) => [
        row.id,
        { auction: row.auction ?? null, marketAuctionScaled: row.marketAuctionScaled ?? null },
      ]),
    );
    const EMPTY_PRICE = { auction: null, marketAuctionScaled: null };
    const withDollars = (rows) =>
      rows.map((row) => ({ ...row, ...(pricedById.get(row.id) || EMPTY_PRICE) }));

    if (rankingBy === 'adp') {
      const byAdp = withDollars([...withValue, ...withoutValue]).sort(
        (a, b) => (a.adp ?? 9999) - (b.adp ?? 9999),
      );
      return withPositionalRanks(assignModelTiers(byAdp));
    }

    if (rankingBy === 'ecr') {
      const byEcr = withDollars([...withValue, ...withoutValue]).sort(
        (a, b) => (a.ecrRank ?? 9999) - (b.ecrRank ?? 9999),
      );
      return withPositionalRanks(assignModelTiers(byEcr));
    }

    withValue.sort((a, b) => b.vor - a.vor);
    withoutValue.sort((a, b) => (a.adp ?? 9999) - (b.adp ?? 9999));
    return withPositionalRanks(assignModelTiers(withDollars(withValue.concat(withoutValue))));
  }, [board, rankingBy, rosterShape, rosterSize, draftInfo, auctionBudget]);

  const boardById = useMemo(() => new Map(rankedBoard.map((r) => [r.id, r])), [rankedBoard]);

  /**
   * How the room is pricing, from the bids that have already closed.
   *
   * Only an auction produces the winning bids this reads, so on a snake draft
   * it stays null and nothing downstream renders.
   */
  const inflation = useMemo(
    () =>
      isAuction
        ? auctionInflation(rankedBoard, draftLog, {
            teams: draftInfo?.teams,
            budget: auctionBudget,
            rosterSize,
          })
        : null,
    [isAuction, rankedBoard, draftLog, draftInfo, auctionBudget, rosterSize],
  );

  const budgets = useMemo(
    () =>
      isAuction
        ? teamBudgets(draftLog, {
            teams: draftInfo?.teams,
            budget: auctionBudget,
            rosterSize,
            teamNames: new Map(teams.map((t) => [String(t.rosterId), t.teamName])),
          })
        : [],
    [isAuction, draftLog, draftInfo, auctionBudget, rosterSize, teams],
  );


  // The gap column is only worth explaining once both feeds have landed.
  const comparedCount = useMemo(
    () => board.filter((r) => r.adpGap != null).length,
    [board],
  );

  const clock = useMemo(
    () => (draftInfo ? draftClock(draftInfo, syncStatus?.pickCount || 0) : null),
    [draftInfo, syncStatus],
  );

  const myRosterId = draftInfo?.myRosterId || rosterId;

  const myBudget = budgets.find((t) => String(t.rosterId) === String(myRosterId)) || null;

  // Load keepers / holdovers once we know which roster is mine.
  useEffect(() => {
    if (!myRosterId || !rankedBoard.length || !leagueIdInput.trim() || draftPlatform !== 'sleeper') {
      return undefined;
    }
    let cancelled = false;
    loadRosterBoardRows(leagueIdInput, myRosterId, rankedBoard)
      .then((rows) => {
        if (!cancelled) setKeeperRows(rows);
      })
      .catch(() => {
        if (!cancelled) setKeeperRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [myRosterId, rankedBoard, leagueIdInput]);

  const myPlayers = useMemo(() => {
    const seen = new Set();
    const rows = [];
    const push = (row) => {
      if (!row || seen.has(row.id)) return;
      seen.add(row.id);
      rows.push(row);
    };

    keeperRows.forEach(push);
    for (const entry of draftLog) {
      const isMine =
        entry.source === 'sleeper' || entry.source === 'espn'
          ? myRosterId != null && String(entry.rosterId) === String(myRosterId)
          : Boolean(entry.mine);
      if (isMine) push(boardById.get(entry.id));
    }
    return rows;
  }, [draftLog, keeperRows, boardById, myRosterId]);

  // How many of my players are already off in each week, so the board can flag
  // a pick that would stack another bye on top.
  const myByeCounts = useMemo(() => {
    const counts = {};
    for (const p of myPlayers) {
      if (p.bye) counts[p.bye] = (counts[p.bye] || 0) + 1;
    }
    return counts;
  }, [myPlayers]);

  const lineup = useMemo(
    () => (rosterShape.starterSlots.length ? computeLineupNeeds(myPlayers, rosterShape) : null),
    [myPlayers, rosterShape],
  );

  const needs = lineup?.needs || [];
  const openStarterSlots = lineup ? lineup.slots.filter((slot) => !slot.player).length : 0;

  const available = useMemo(
    () =>
      filterBoard(rankedBoard, { draftedIds, position: positionFilter, search })
        // Stamp the ranking position before any display sort, so the # column
        // keeps meaning "where this player sits on the board" no matter which
        // column the table is currently sorted by.
        .map((row, index) => ({ ...row, boardRank: index + 1 })),
    [rankedBoard, draftedIds, positionFilter, search],
  );

  // Odds are computed against the unfiltered board so a position filter does not
  // make everyone look safe.
  const allAvailable = useMemo(
    () => filterBoard(rankedBoard, { draftedIds, position: 'ALL', search: '' }),
    [rankedBoard, draftedIds],
  );

  /**
   * The pick we are asking "will he last until then?" about.
   *
   * On the clock that is the pick after this one; while waiting it is my next
   * turn. Without a connected draft we assume one full snake turn, which keeps
   * the survival and wait-cost columns live in the default state instead of
   * showing a column of em-dashes.
   */
  const teamCount = draftInfo?.teams || leagueMeta?.totalRosters || 12;
  const targetPick = clock
    ? (clock.isMyPick ? clock.followingPick?.pickNo : clock.nextPick?.pickNo) ?? null
    : teamCount * 2;
  const oddsWindow = targetPick;

  const oddsById = useMemo(() => {
    const withOdds = withWaitCost(withAvailabilityOdds(allAvailable, targetPick));
    return new Map(withOdds.map((r) => [r.id, r]));
  }, [allAvailable, targetPick]);

  /**
   * What the table actually renders: the available board, carrying its
   * survival odds, ordered by whichever column the user clicked. Sorting here
   * is presentation only — odds and recommendations stay anchored to the
   * board's ranking order.
   */
  const displayRows = useMemo(() => {
    const withOdds = available.map((row) => {
      const odds = oddsById.get(row.id);
      return odds
        ? { ...row, pGone: odds.pGone, availability: odds.availability, waitCost: odds.waitCost }
        : row;
    });
    return sort.key ? sortBoardRows(withOdds, sort.key, sort.dir) : withOdds;
  }, [available, oddsById, sort]);

  const handleSort = useCallback((column) => {
    setSort((current) => nextSortState(column, current));
  }, []);

  const recommendations = useMemo(() => {
    if (!allAvailable.length) return [];
    const withOdds = allAvailable.map((r) => oddsById.get(r.id) || r);
    return recommendPicks({
      availableWithOdds: withOdds,
      needs,
      roundsRemaining: clock?.roundsRemaining ?? null,
      openStarterSlots,
      limit: 5,
    });
  }, [allAvailable, oddsById, needs, clock, openStarterSlots]);

  const scarcity = useMemo(
    () => computePositionScarcity(rankedBoard, draftedIds),
    [rankedBoard, draftedIds],
  );
  const cliff = useMemo(
    () => (rankingBy === 'value' ? nextTierCliff(available) : null),
    [available, rankingBy],
  );

  const needPriority = useMemo(
    () => Object.fromEntries(needs.map((n) => [n.position, n.priority])),
    [needs],
  );

  const countdown = draftInfo?.startTime ? formatCountdown(draftInfo.startTime, now) : '';

  /* ---------------- actions ---------------- */

  const markDrafted = useCallback(
    (id, { mine = false } = {}) => {
      const row = boardById.get(id);
      if (!row || draftedIds.has(id)) return;
      setDraftedIds((prev) => new Set([...prev, id]));
      setDraftLog((prev) => [
        { id, name: row.name, position: row.position, team: row.team, value: row.vor, at: Date.now(), mine },
        ...prev,
      ]);
    },
    [boardById, draftedIds],
  );

  const undoLast = useCallback(() => {
    setDraftLog((prev) => {
      if (!prev.length) return prev;
      const [last, ...rest] = prev;
      setDraftedIds((ids) => {
        const next = new Set(ids);
        next.delete(last.id);
        return next;
      });
      return rest;
    });
  }, []);

  const resetDraft = useCallback(() => {
    setDraftedIds(new Set());
    setDraftLog([]);
  }, []);

  const toggleWatch = useCallback((id) => {
    setWatchlist((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleSync = () => {
    if (draftPlatform === 'espn') {
      if (!leagueIdInput.trim() || !espnSeasonInput.trim()) {
        setSyncError('Connect an ESPN league with a season year first.');
        return;
      }
    } else if (!draftId) {
      setSyncError('Connect a Sleeper league with a draft room first.');
      return;
    }
    setSyncError('');
    setSyncEnabled((prev) => !prev);
  };

  const copyDraftLog = async () => {
    if (!draftLog.length) return;
    const lines = draftLog
      .slice()
      .reverse()
      .map((e, i) => {
        const pickInfo =
          e.pickNo != null ? `#${e.pickNo}${e.teamName ? ` · ${e.teamName}` : ''} · ` : '';
        return `${i + 1}. ${pickInfo}${e.name} (${e.position})`;
      });
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch {
      /* clipboard unavailable */
    }
  };

  const watchlistRows = useMemo(
    () =>
      [...watchlist]
        .map((id) => boardById.get(id))
        .filter(Boolean)
        .filter((r) => !draftedIds.has(r.id))
        // Consensus order, which is the board's own order out of the box.
        .sort((a, b) => (a.ecrRank ?? 9999) - (b.ecrRank ?? 9999)),
    [watchlist, boardById, draftedIds],
  );

  const myTeamName =
    teams.find((t) => String(t.rosterId) === String(myRosterId))?.teamName || 'Your team';

  /* ---------------- render ---------------- */

  const renderClockStrip = () => {
    if (!clock || !draftInfo) {
      return (
        <div className="draft-clock draft-clock--idle">
          <span className="draft-clock__label">
            {loadingLeague
              ? `Connecting to ${draftPlatform === 'espn' ? 'ESPN' : 'Sleeper'}…`
              : `Not connected to a ${draftPlatform === 'espn' ? 'ESPN' : 'Sleeper'} draft room yet.`}
          </span>
        </div>
      );
    }

    if (clock.isComplete) {
      return (
        <div className="draft-clock draft-clock--done">
          <span className="draft-clock__label">Draft complete</span>
          <span className="draft-clock__value">{draftInfo.rounds} rounds · all picks in</span>
        </div>
      );
    }

    /**
     * An auction has no clock in the sense the rest of this strip means.
     *
     * Nobody is "on the clock" for a pick that belongs to them, there is no
     * wheel, and every player is yours to bid on at every moment — so the
     * snake numbers below are not merely unhelpful here, they are answers to
     * questions the format does not ask. What replaces them is the only
     * constraint an auction actually has: money, yours and everyone else's.
     */
    if (isAuction) {
      const rate = inflation?.rate ?? null;
      const hot = rate != null && Math.abs(rate - 1) >= RATE_NOISE;
      return (
        <div className="draft-clock draft-clock--auction">
          <div className="draft-clock__cell draft-clock__cell--headline">
            <span className="draft-clock__label">Your budget</span>
            <span className="draft-clock__value draft-clock__value--big">
              {myBudget ? `$${myBudget.left}` : `$${auctionBudget}`}
            </span>
          </div>

          <div className="draft-clock__cell">
            <span className="draft-clock__label">Most you can bid</span>
            <span
              className="draft-clock__value"
              title={
                myBudget
                  ? `$${myBudget.left} left with ${myBudget.spotsLeft} roster spots to fill — ` +
                    'every one of them still costs at least a dollar, so that much is not ' +
                    'biddable on this player.'
                  : 'Your budget, less a dollar held back for every roster spot after this one.'
              }
            >
              {myBudget ? `$${myBudget.maxBid} · ${myBudget.spotsLeft} spots left` : '—'}
            </span>
          </div>

          <div className="draft-clock__cell">
            <span className="draft-clock__label">Room is paying</span>
            <span
              className={`draft-clock__value ${hot ? 'is-hot' : ''}`}
              title={formatInflationTooltip(inflation)}
            >
              {rate == null
                ? `${inflation?.bids || 0} bids in — too early to say`
                : `${Math.round(rate * 100)}% of board price`}
            </span>
          </div>

          <div className="draft-clock__cell">
            <span className="draft-clock__label">Running hot</span>
            <span
              className={`draft-clock__value ${
                positionOutliers(inflation).length ? 'is-hot' : ''
              }`}
              title={formatPositionTooltip(inflation)}
            >
              {formatPositionHeat(inflation) || 'Too early to say'}
            </span>
          </div>

          <div className="draft-clock__cell">
            <span className="draft-clock__label">Left in the room</span>
            <span className="draft-clock__value">
              {inflation ? `$${inflation.moneyLeft} · ${inflation.spotsLeft} spots` : '—'}
            </span>
          </div>

          {budgets.length > 0 && (
            <div className="draft-clock__cell">
              <span className="draft-clock__label">Who can outbid you</span>
              <span
                className="draft-clock__value"
                title={budgets
                  .map((t) => `${t.teamName}: $${t.left} left, $${t.maxBid} max, ${t.spotsLeft} spots`)
                  .join('\n')}
              >
                {(() => {
                  const mine = myBudget?.maxBid ?? 0;
                  const others = budgets.filter((t) => String(t.rosterId) !== String(myRosterId));
                  const over = others.filter((t) => t.maxBid > mine);
                  if (over.length) {
                    return `${over.length} team${over.length === 1 ? '' : 's'} · top $${over[0].maxBid}`;
                  }
                  const level = others.filter((t) => t.maxBid === mine).length;
                  return level
                    ? `Nobody — ${level} still level with you at $${mine}`
                    : 'Nobody — you have the biggest bid in the room';
                })()}
              </span>
            </div>
          )}

          {countdown && draftInfo.status === 'pre_draft' && (
            <div className="draft-clock__cell">
              <span className="draft-clock__label">Starts in</span>
              <span className="draft-clock__value">{countdown}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`draft-clock ${clock.isMyPick ? 'is-my-pick' : ''}`}>
        <div className="draft-clock__cell">
          <span className="draft-clock__label">On the clock</span>
          <span className="draft-clock__value">
            {formatPickSlot(clock.currentPickNo, draftInfo.teams)} · {clock.onTheClockTeam}
          </span>
        </div>

        <div className="draft-clock__cell">
          <span className="draft-clock__label">Your next pick</span>
          <span className="draft-clock__value">
            {clock.nextPick
              ? `${formatPickSlot(clock.nextPick.pickNo, draftInfo.teams)} (#${clock.nextPick.pickNo})`
              : 'No picks left'}
          </span>
        </div>

        <div className="draft-clock__cell draft-clock__cell--headline">
          <span className="draft-clock__label">
            {clock.isMyPick ? 'Status' : 'Picks until your turn'}
          </span>
          <span className="draft-clock__value draft-clock__value--big">
            {clock.isMyPick ? 'You are up' : (clock.picksUntilMyTurn ?? '—')}
          </span>
        </div>

        <div className="draft-clock__cell">
          <span className="draft-clock__label">Wheel gap</span>
          <span className="draft-clock__value">
            {clock.picksBetweenMyTurns != null
              ? `${clock.picksBetweenMyTurns} picks between your turns`
              : '—'}
          </span>
        </div>

        {countdown && draftInfo.status === 'pre_draft' && (
          <div className="draft-clock__cell">
            <span className="draft-clock__label">Starts in</span>
            <span className="draft-clock__value">{countdown}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <article className="draft-tool" id="draft-tool">
      <header className="draft-tool__header">
        <div className="draft-tool__header-row">
          <div>
            <p className="dashboard__eyebrow">Redraft</p>
            <h2 className="draft-tool__title">Draft assistant</h2>
          </div>
          <button
            type="button"
            className={`draft-tool__focus-toggle ${focusMode ? 'is-active' : ''}`}
            onClick={() => setFocusMode((prev) => !prev)}
            aria-pressed={focusMode}
            title={
              focusMode
                ? 'Show the rest of the page again'
                : 'Hide everything except the draft board'
            }
          >
            {focusMode ? '◆ Focus on' : '◇ Focus off'}
          </button>
        </div>
        <p className="draft-tool__lede">
          {leagueMeta ? (
            <>
              Connected to <strong>{leagueMeta.name}</strong> ({leagueMeta.season}) as{' '}
              <strong>{myTeamName}</strong> — {leagueMeta.totalRosters} teams,{' '}
              {leagueMeta.scoringLabel}, {rosterShape.format === 'sf' ? 'superflex' : '1 QB'}.
              {syncEnabled
                ? ` Picks sync live from your ${draftPlatform === 'espn' ? 'ESPN' : 'Sleeper'} draft room.`
                : ` Start live sync when your ${draftPlatform === 'espn' ? 'ESPN' : 'Sleeper'} draft begins.`}
            </>
          ) : (
            <>
              Expert consensus against the market&apos;s ADP, with the age, offence and last-season
              finish behind the gap — plus live Sleeper draft sync, positional scarcity, and
              roster needs.
            </>
          )}
        </p>
      </header>

      {renderClockStrip()}

      <div className="draft-tool__layout">
        <aside className="draft-tool__sidebar dashboard__panel">
          <div className="dashboard-form">
            <section className="draft-tool__recs" aria-label="Recommended picks">
              <h3 className="draft-tool__section-title">
                {clock?.isMyPick ? 'Take one of these' : 'Best picks right now'}
              </h3>
              {loadingBoard ? (
                <p className="draft-tool__placeholder">Loading rankings…</p>
              ) : !recommendations.length ? (
                <p className="draft-tool__placeholder">No players left on the board.</p>
              ) : (
                <ol className="draft-tool__rec-list">
                  {recommendations.map((rec, index) => (
                    <li
                      key={rec.id}
                      className={`draft-tool__rec is-${rec.needPriority} ${
                        rec.required ? 'is-required' : ''
                      }`}
                    >
                      <div className="draft-tool__rec-head">
                        <span className="draft-tool__rec-rank">{index + 1}</span>
                        <BoardAvatar row={rec} map={headshotMap} size="md" />
                        <span className="draft-tool__rec-name">{rec.name}</span>
                        <span className="draft-tool__rec-pos">
                          {rec.position}
                          {rec.posRank != null ? `${rec.posRank}` : ''} · {rec.team}
                        </span>
                      </div>
                      {(rec.proj != null || rec.waitCost != null || rec.bye != null) && (
                        <p className="draft-tool__rec-stats">
                          {rec.proj != null && <strong>{Math.round(rec.proj)} pts</strong>}
                          {rec.waitCost != null && (
                            <>
                              {' · '}
                              <span
                                className={rec.waitCost >= 25 ? 'draft-tool__rec-urgent' : ''}
                                title="What this pick buys over waiting until your next turn"
                              >
                                {rec.waitCost < 5
                                  ? 'cheap to wait'
                                  : `worth ${Math.round(rec.waitCost)} over waiting`}
                              </span>
                            </>
                          )}
                          {rec.modelEdge ? (
                            <>
                              {' · '}
                              <span title={`Our disagreement with the market, after the confidence gate`}>
                                {rec.modelEdge > 0 ? '+' : ''}
                                {Math.round(rec.modelEdge)} vs market
                              </span>
                            </>
                          ) : null}
                          {!isAuction &&
                          rec.adpGap != null &&
                          Math.abs(rec.adpGap) >= ADP_GAP_NOISE ? (
                            <>
                              {' · '}
                              <span
                                className={`draft-tool__rec-gap ${
                                  rec.adpGap > 0 ? 'is-falls' : 'is-reach'
                                }`}
                                title={formatGapTooltip(rec)}
                              >
                                {rec.adpGap > 0
                                  ? `falls ${rec.adpGap} past consensus`
                                  : `costs a ${Math.abs(rec.adpGap)}-slot reach`}
                              </span>
                            </>
                          ) : null}
                          {rec.bye != null && ' · '}
                          {rec.bye != null && (
                            <span className={myByeCounts[rec.bye] >= 2 ? 'is-bye-clash' : ''}>
                              bye {rec.bye}
                            </span>
                          )}

                        </p>
                      )}
                      <p className="draft-tool__rec-why">{rec.reasons.join(' · ')}</p>
                      <div className="draft-tool__rec-foot">
                        <span className={`draft-tool__odds is-${rec.availability.level}`}>
                          {rec.availability.text}
                          {oddsWindow ? ` · ${Math.round(rec.pGone * 100)}% gone` : ''}
                        </span>
                        {syncEnabled ? (
                          <button
                            type="button"
                            className={`draft-tool__rec-take ${
                              watchlist.has(rec.id) ? 'is-watched' : ''
                            }`}
                            onClick={() => toggleWatch(rec.id)}
                            title={
                              watchlist.has(rec.id)
                                ? `Remove ${rec.name} from your watchlist`
                                : `Watch ${rec.name} — picks are recorded by live sync`
                            }
                          >
                            {watchlist.has(rec.id) ? '★ Watching' : '★ Watch'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="draft-tool__rec-take"
                            onClick={() => markDrafted(rec.id, { mine: true })}
                            title={`Add ${rec.name} to your roster`}
                          >
                            Take
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {lineup && (
              <section className="draft-tool__lineup" aria-label="Your starting lineup">
                <h3 className="draft-tool__section-title">
                  {myTeamName} · {myPlayers.length} player{myPlayers.length === 1 ? '' : 's'}
                </h3>
                <ul className="draft-tool__lineup-list">
                  {lineup.slots.map((slotEntry) => (
                    <li
                      key={slotEntry.key}
                      className={`draft-tool__lineup-row ${slotEntry.player ? 'is-filled' : 'is-open'}`}
                    >
                      <span className="draft-tool__lineup-slot">{slotLabel(slotEntry.slot)}</span>
                      <span className="draft-tool__lineup-player">
                        {slotEntry.player ? slotEntry.player.name : 'open'}
                      </span>
                    </li>
                  ))}
                </ul>
                <ul className="draft-tool__needs-list">
                  {needs.map((n) => (
                    <li key={n.position} className={`draft-tool__need is-${n.priority}`}>
                      <span>{n.position}</span>
                      <span>
                        {n.have}/{n.target}
                        {n.openStarters > 0
                          ? ` · ${n.openStarters} starter slot${n.openStarters === 1 ? '' : 's'} open`
                          : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="dashboard-form__field">
              <label htmlFor="draft-search">Search board</label>
              <input
                id="draft-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Player name"
                autoComplete="off"
              />
            </div>

            <fieldset className="dashboard-form__fieldset" aria-label="Position filter">
              <legend className="dashboard-form__legend">Position</legend>
              <div className="draft-tool__chips" role="group">
                {DRAFT_POSITIONS.map((pos) => (
                  <button
                    key={pos}
                    type="button"
                    className={`draft-tool__chip ${positionFilter === pos ? 'is-active' : ''}`}
                    onClick={() => setPositionFilter(pos)}
                    aria-pressed={positionFilter === pos}
                  >
                    {pos}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="draft-tool__stats">
              <div className="draft-tool__stat">
                <span className="draft-tool__stat-label">Available</span>
                <span className="draft-tool__stat-value">{available.length}</span>
              </div>
              <div className="draft-tool__stat">
                <span className="draft-tool__stat-label">Drafted</span>
                <span className="draft-tool__stat-value">{draftedIds.size}</span>
              </div>
              <div className="draft-tool__stat">
                <span className="draft-tool__stat-label">Watchlist</span>
                <span className="draft-tool__stat-value">{watchlistRows.length}</span>
              </div>
            </div>

            {cliff && (
              <div className="draft-tool__alert" role="status">
                <strong>Tier cliff:</strong> {cliff.picksUntilCliff} pick
                {cliff.picksUntilCliff === 1 ? '' : 's'} until value drops from{' '}
                <em>{cliff.player}</em> to tier {available[cliff.picksUntilCliff]?.tier}
                {cliff.valueGap != null ? ` (${Math.round(cliff.valueGap)} pts of value).` : '.'}
              </div>
            )}

            <section className="draft-tool__scarcity" aria-label="Position scarcity">
              <h3 className="draft-tool__section-title">Top-48 scarcity</h3>
              <ul className="draft-tool__scarcity-list">
                {scarcity.map((s) => (
                  <li key={s.position} className={`draft-tool__scarcity-row is-${s.level}`}>
                    <span>{s.position}</span>
                    <span>
                      {s.remaining}/{s.total} left
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="draft-tool__sync" aria-label="Live draft sync">
              <div className="draft-tool__sync-head">
                <h3 className="draft-tool__section-title">Live draft sync</h3>
                <button
                  type="button"
                  className="draft-tool__link-btn"
                  onClick={() => setShowSettings((prev) => !prev)}
                  aria-expanded={showSettings}
                >
                  {showSettings ? 'Hide setup' : 'League setup'}
                </button>
              </div>

              <div className="draft-tool__rankby" role="group" aria-label="Draft platform">
                <button
                  type="button"
                  className={`draft-tool__rankby-btn ${draftPlatform === 'sleeper' ? 'is-active' : ''}`}
                  onClick={() => setDraftPlatform('sleeper')}
                  aria-pressed={draftPlatform === 'sleeper'}
                >
                  Sleeper
                </button>
                <button
                  type="button"
                  className={`draft-tool__rankby-btn ${draftPlatform === 'espn' ? 'is-active' : ''}`}
                  onClick={() => setDraftPlatform('espn')}
                  aria-pressed={draftPlatform === 'espn'}
                >
                  ESPN
                </button>
              </div>

              <div
                className={`draft-tool__sync-status ${syncEnabled ? 'is-live' : ''}`}
                role="status"
                aria-live="polite"
              >
                <span className="draft-tool__sync-dot" aria-hidden="true" />
                <span>
                  {syncEnabled
                    ? `Syncing${syncStatus?.pickCount != null ? ` · ${syncStatus.pickCount} picks` : ''}${
                        syncStatus?.totalPicks ? ` of ${syncStatus.totalPicks}` : ''
                      }${syncStatus?.status ? ` · ${syncStatus.status.replace(/_/g, ' ')}` : ''}`
                    : 'Sync off — mark picks manually or start live sync'}
                </span>
              </div>

              <div className="draft-tool__sync-actions">
                <button
                  type="button"
                  className={`dashboard-form__submit ${syncEnabled ? 'draft-tool__btn-danger' : ''}`}
                  onClick={handleToggleSync}
                  disabled={
                    loadingBoard ||
                    (draftPlatform === 'espn'
                      ? !leagueIdInput.trim() || !espnSeasonInput.trim()
                      : !draftId)
                  }
                >
                  {syncEnabled ? 'Stop sync' : 'Start live sync'}
                </button>
                <button
                  type="button"
                  className="dashboard-form__submit draft-tool__btn-secondary"
                  onClick={() => connectLeague()}
                  disabled={loadingLeague}
                >
                  {loadingLeague ? 'Loading…' : 'Reconnect'}
                </button>
              </div>

              {lastSyncAt && syncEnabled && (
                <p className="draft-tool__sync-time">Last synced {lastSyncAt.toLocaleTimeString()}</p>
              )}

              {syncError && (
                <p className="dashboard-form__error" role="alert">
                  {syncError}
                </p>
              )}

              {showSettings && (
                <div className="draft-tool__settings">
                  {draftPlatform === 'espn' ? (
                    <>
                      <div className="dashboard-form__field">
                        <label htmlFor="draft-espn-league-id">ESPN league ID</label>
                        <input
                          id="draft-espn-league-id"
                          type="text"
                          value={espnLeagueIdInput}
                          onChange={(e) => setEspnLeagueIdInput(e.target.value)}
                          placeholder="From your ESPN league URL"
                          autoComplete="off"
                        />
                      </div>
                      <div className="dashboard-form__field">
                        <label htmlFor="draft-espn-season">Season year</label>
                        <input
                          id="draft-espn-season"
                          type="text"
                          value={espnSeasonInput}
                          onChange={(e) => setEspnSeasonInput(e.target.value)}
                          placeholder="e.g. 2025"
                          autoComplete="off"
                        />
                      </div>
                      <p className="draft-tool__hint">
                        Public leagues work out of the box. Private leagues need you logged into ESPN
                        in this browser.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="dashboard-form__field">
                        <label htmlFor="draft-league-id">Sleeper league ID</label>
                        <input
                          id="draft-league-id"
                          type="text"
                          value={sleeperLeagueIdInput}
                          onChange={(e) => setSleeperLeagueIdInput(e.target.value)}
                          placeholder="From your Sleeper league URL"
                          autoComplete="off"
                        />
                      </div>
                      <div className="dashboard-form__field">
                        <label htmlFor="draft-username">Your Sleeper username</label>
                        <input
                          id="draft-username"
                          type="text"
                          value={usernameInput}
                          onChange={(e) => setUsernameInput(e.target.value)}
                          placeholder="Used to find your team"
                          autoComplete="off"
                        />
                      </div>
                    </>
                  )}
                  <div className="dashboard-form__field">
                    <label htmlFor="draft-team">Your team</label>
                    <select
                      id="draft-team"
                      value={String(rosterId)}
                      onChange={(e) => setRosterId(e.target.value)}
                      disabled={!teams.length}
                    >
                      {!teams.length ? (
                        <option value="">Connect a league first…</option>
                      ) : (
                        teams.map((t) => (
                          <option key={t.rosterId} value={String(t.rosterId)}>
                            {t.teamName}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {draftPlatform === 'sleeper' && (
                    <div className="dashboard-form__field">
                      <label htmlFor="draft-sync-draft">Draft room</label>
                      <select
                        id="draft-sync-draft"
                        value={draftId}
                        onChange={(e) => setDraftId(e.target.value)}
                        disabled={!drafts.length}
                      >
                        {!drafts.length ? (
                          <option value="">No drafts found…</option>
                        ) : (
                          drafts.map((d) => (
                            <option key={d.draft_id} value={d.draft_id}>
                              {formatDraftLabel(d)}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                  <div className="dashboard-form__field">
                    <label htmlFor="draft-format">Value set</label>
                    <select
                      id="draft-format"
                      value={format}
                      onChange={(e) => {
                        setFormat(e.target.value === 'sf' ? 'sf' : 'oneqb');
                        setFormatLocked(true);
                      }}
                    >
                      <option value="oneqb">1 QB</option>
                      <option value="sf">Superflex</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="dashboard-form__submit draft-tool__btn-secondary"
                    onClick={() => connectLeague()}
                    disabled={loadingLeague}
                  >
                    {loadingLeague ? 'Loading…' : 'Apply & reconnect'}
                  </button>
                </div>
              )}

              {unmatchedPicks.length > 0 && (
                <div className="draft-tool__unmatched">
                  <p className="draft-tool__unmatched-title">
                    {unmatchedPicks.length} pick{unmatchedPicks.length === 1 ? '' : 's'} not on
                    redraft board
                  </p>
                  <ul className="draft-tool__unmatched-list">
                    {unmatchedPicks.slice(0, 5).map((p) => (
                      <li key={`${p.pickNo}-${p.name}`}>
                        #{p.pickNo} {p.name} ({p.position}) → {p.teamName}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <div className="draft-tool__sidebar-actions">
              <button
                type="button"
                className="dashboard-form__submit draft-tool__btn-secondary"
                onClick={undoLast}
                disabled={!draftLog.length || syncEnabled}
                title={syncEnabled ? 'Turn off live sync to undo manually' : undefined}
              >
                Undo last pick
              </button>
              <button
                type="button"
                className="dashboard-form__submit draft-tool__btn-danger"
                onClick={resetDraft}
                disabled={!draftLog.length || syncEnabled}
                title={syncEnabled ? 'Turn off live sync to reset manually' : undefined}
              >
                Reset draft
              </button>
            </div>

            {(error || boardError) && (
              <p className="dashboard-form__error" role="alert">
                {error || boardError}
              </p>
            )}
          </div>
        </aside>

        <div className="draft-tool__main">
          <section className="dashboard__panel draft-tool__board-panel">
            <header className="draft-tool__board-head">
              <div>
                <div className="draft-tool__board-title-row">
                  <h3 className="draft-tool__board-title">Best available</h3>
                  {rankControlsAvailable && (
                    <div className="draft-tool__rankby" role="group" aria-label="Rank board by">
                      {ecrAvailable && (
                        <button
                          type="button"
                          className={`draft-tool__rankby-btn ${rankBy === 'ecr' ? 'is-active' : ''}`}
                          onClick={() => setRankBy('ecr')}
                          aria-pressed={rankBy === 'ecr'}
                        >
                          ECR
                        </button>
                      )}
                      {projectionsAvailable && (
                        <button
                          type="button"
                          className={`draft-tool__rankby-btn ${rankBy === 'value' ? 'is-active' : ''}`}
                          onClick={() => setRankBy('value')}
                          aria-pressed={rankBy === 'value'}
                        >
                          Our value
                        </button>
                      )}
                      <button
                        type="button"
                        className={`draft-tool__rankby-btn ${rankBy === 'adp' ? 'is-active' : ''}`}
                        onClick={() => setRankBy('adp')}
                        aria-pressed={rankBy === 'adp'}
                      >
                        ADP
                      </button>
                    </div>
                  )}
                </div>
                <p className="draft-tool__board-meta">
                  {format === 'sf' ? 'Superflex' : '1 QB'} ·{' '}
                  {rankingBy === 'value'
                    ? `ranked by ${SCORING_LABELS[scoringKey]} points over replacement`
                    : rankingBy === 'ecr'
                      ? 'ranked by FantasyPros expert consensus (ECR)'
                      : 'ranked by average draft position'}
                  {` · ${board.length} players`}
                  {syncEnabled ? ' · live sync on' : ''}
                </p>
                {board.length > 0 && (
                  <p className="draft-tool__hint">
                    {[
                      'ECR is the expert consensus, Pos is his rank at his own position',
                      'ADP is what the room is paying',
                      comparedCount
                        ? `vs ADP is the gap between them in board slots, over the ${comparedCount} players carrying both — green means he falls past consensus, red means owning him costs a reach`
                        : null,
                      'Age is on opening day',
                      priorSeason
                        ? `Off is his offence's ${priorSeason} scoring rank, LY is where he finished at his position that season`
                        : null,
                      `$ is what he is worth in a ${teamCount}-team, $${auctionBudget} auction under this lineup${
                        draftInfo?.budget ? '' : ' (the standard budget — connect an auction draft for yours)'
                      }`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </div>
              {watchlistRows.length > 0 && (
                <div className="draft-tool__watch-banner">
                  <span className="draft-tool__watch-label">On your list:</span>
                  {watchlistRows.slice(0, 4).map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="draft-tool__watch-pill"
                      onClick={() => markDrafted(r.id, { mine: true })}
                      title={`Draft ${r.name}`}
                    >
                      <BoardAvatar row={r} map={headshotMap} size="xs" />
                      <span className="ff-avatar-name__label">★ {r.name}</span>
                    </button>
                  ))}
                  {watchlistRows.length > 4 && (
                    <span className="draft-tool__watch-more">+{watchlistRows.length - 4} more</span>
                  )}
                </div>
              )}
            </header>

            {loadingBoard ? (
              <div className="dashboard-results__loading" role="status" aria-live="polite">
                <span className="dashboard-results__spinner" aria-hidden="true" />
                <p>Loading the board…</p>
              </div>
            ) : (
              <div className="draft-table-wrap">
                <table className="draft-table">
                  <thead>
                    <tr>
                      {[
                        { key: 'rank', title: 'Position on the board under the current ranking' },
                        { key: 'name' },
                        { key: 'team' },
                        ...(isAuction
                          ? [
                              {
                                key: 'auction',
                                title:
                                  'What he costs on this league\u2019s budget \u2014 ESPN\u2019s published auction value scaled from their ten-team, $200 board onto this one. Once enough bids have closed this becomes tonight\u2019s price, with the list price struck through beside it. Sleeper publishes no auction values or ADP of its own, so there is no Sleeper number to show here.',
                              },
                            ]
                          : []),
                        {
                          key: 'adp',
                          title:
                            'Average draft position \u2014 where the room is actually taking him. Hover a cell for the round and pick, and how much real drafts scatter around it.',
                        },
                        {
                          key: 'ecr',
                          title:
                            'FantasyPros expert consensus rank. Hover a cell for his tier and how far the experts themselves disagreed.',
                        },
                        {
                          key: 'posRank',
                          title:
                            'Where the consensus puts him at his own position. Sorting groups the board by position, then orders each one.',
                        },
                        // A rank gap is the wrong unit for an auction: there
                        // is no draft order to fall through, and the board
                        // carries only one dollar series now, so there is no
                        // second price to subtract from it either.
                        ...(isAuction
                          ? []
                          : [
                              {
                                key: 'adpGap',
                                title:
                                  'How far the room and the experts disagree, in board slots. Green means the room drafts him later than the experts rank him, so he falls to you; red means owning him costs a reach. Both lists are re-ranked over the players that carry both numbers, which is the only way the two are subtractable.',
                              },
                            ]),
                        {
                          key: 'age',
                          title:
                            'Age on opening day. The same consensus rank on a 23-year-old and a 31-year-old are not the same bet.',
                        },
                        {
                          key: 'offense',
                          title:
                            'His offence, ranked 1\u201332 by points per game last season \u2014 offensive scoring only, with defensive and return touchdowns excluded. Joined on the team he is on now, so a trade shows up immediately.',
                        },
                        {
                          key: 'lastYear',
                          title:
                            'Where he finished at his position last season, by total half-PPR points. Hover a cell for the points and the games it took.',
                        },
                        ...(isAuction
                          ? []
                          : [
                              {
                                key: 'auction',
                                title:
                                  'What he costs on this league\u2019s budget \u2014 ESPN\u2019s published auction value scaled from their ten-team, $200 board onto this one.',
                              },
                            ]),
                      ].map(({ key, label, title }) => {
                        const active = sort.key === key;
                        return (
                          <th
                            key={key}
                            scope="col"
                            aria-sort={
                              active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                            }
                          >
                            <button
                              type="button"
                              className={`draft-table__sort ${active ? 'is-active' : ''}`}
                              onClick={() => handleSort(key)}
                              title={title}
                            >
                              {label || BOARD_COLUMNS[key]?.label || key}
                              <span className="draft-table__sort-arrow" aria-hidden="true">
                                {active ? (sort.dir === 'asc' ? '\u25b2' : '\u25bc') : '\u2195'}
                              </span>
                            </button>
                          </th>
                        );
                      })}
                      {!syncEnabled && <th scope="col" aria-label="Actions" />}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.slice(0, focusMode ? 250 : 120).map((row, idx) => {
                      const prev = displayRows[idx - 1];
                      // Tier bands only mean something while the table is in the
                      // board's own KTC order; any other sort interleaves tiers.
                      const showTierBreak =
                        rankingBy === 'value' && !sort.key && (!prev || prev.tier !== row.tier);
                      const needLevel = needPriority[row.position];
                      const odds = oddsById.get(row.id);
                      const ecrCell = (
  <td
                                className="draft-table__num draft-table__ecr"
                                title={formatEcrTooltip(row)}
                              >
                                {row.ecrRank ?? <span className="draft-table__muted">—</span>}
                              </td>
                      );
                      const posCell = (
  <td className="draft-table__pos">
                                {row.position}
                                {row.ecrPosRank != null && (
                                  <span className="draft-table__posrank">{row.ecrPosRank}</span>
                                )}
                              </td>
                      );
                      const adpCell = (
  <td
                                className="draft-table__num draft-table__adp"
                                title={
                                  row.adp == null
                                    ? 'Nobody in the sample drafted him — too deep to have an ADP'
                                    : [
                                        `Goes around ${formatAdpSlot(row.adp, teamCount)} in a ${teamCount}-team league`,
                                        row.adpStdev != null
                                          ? `Real drafts scatter about ${row.adpStdev.toFixed(1)} picks either side`
                                          : null,
                                        row.adpTimesDrafted != null
                                          ? `From ${row.adpTimesDrafted} drafts`
                                          : null,
                                      ]
                                        .filter(Boolean)
                                        .join('\n')
                                }
                              >
                                {row.adp != null ? row.adp.toFixed(1) : '\u2014'}
                              </td>
                      );
                      // Tonight's price is the listed one moved by this
                      // player's own position, not by the room average: the
                      // room average is what backs and receivers are doing to
                      // each other, and averaging over it hides the whole
                      // signal from the one row that needs it.
                      const rowRate = positionRate(inflation, row.position);
                      const tonight =
                        rowRate != null ? inflatedPrice(row.auction, rowRate) : row.auction;
                      const dollarCell = (
  <td
                                className="draft-table__num draft-table__auction"
                                title={[
                                  formatAuctionTooltip(row, {
                                    budget: auctionBudget,
                                    teams: teamCount,
                                  }),
                                  formatRowInflationNote(inflation, row),
                                ]
                                  .filter(Boolean)
                                  .join('\n')}
                              >
                                {row.auction == null ? (
                                  '\u2014'
                                ) : tonight !== row.auction ? (
                                  <>
                                    {`$${tonight}`}
                                    <span
                                      className="draft-table__muted draft-table__auction-list"
                                      title={`$${row.auction} on this board before the room's pricing is applied`}
                                    >
                                      {` $${row.auction}`}
                                    </span>
                                  </>
                                ) : (
                                  `$${row.auction}`
                                )}
                              </td>
                      );
                      return (
                        <Fragment key={row.id}>
                          {showTierBreak && (
                            <tr className="draft-table__tier-row">
                              <td colSpan={11 + (syncEnabled ? 0 : 1)}>
                                Tier {row.tier}
                                {['critical', 'high'].includes(needLevel) &&
                                  !['K', 'DST'].includes(row.position) && (
                                    <span className="draft-table__need-tag">
                                      {' '}
                                      — you need {row.position}
                                    </span>
                                  )}
                              </td>
                            </tr>
                          )}
                          <tr
                            className={`${watchlist.has(row.id) ? 'is-watched' : ''} ${
                              ['critical', 'high'].includes(needLevel) ? 'is-need' : ''
                            }`}
                          >
                            <td className="draft-table__num">{row.boardRank}</td>
                            <td>
                              <span className="draft-table__name">
                                <BoardAvatar row={row} map={headshotMap} />
                                <span className="ff-avatar-name__label">{row.name}</span>
                              </span>
                            </td>
                            <td>{row.team}</td>
                            {isAuction ? (
                              <>
                                {dollarCell}
                                {adpCell}
                                {ecrCell}
                                {posCell}
                              </>
                            ) : (
                              <>
                                {ecrCell}
                                {posCell}
                                {adpCell}
    {!isAuction && (
                                  <td
                                    className={`draft-table__num draft-table__rankgap is-${gapLevel(row)}`}
                                    title={formatGapTooltip(row)}
                                  >
                                    {row.adpGap == null ? (
                                      <span className="draft-table__muted">—</span>
                                    ) : Math.abs(row.adpGap) < ADP_GAP_NOISE ? (
                                      <span className="draft-table__muted">·</span>
                                    ) : (
                                      `${row.adpGap > 0 ? '+' : ''}${row.adpGap}`
                                    )}
                                  </td>
                                )}
                              </>
                            )}
                            <td
                              className="draft-table__num draft-table__age"
                              title={
                                row.age == null
                                  ? 'No birth date on any roster we read'
                                  : `${row.age} on opening day${
                                      row.yearsExp != null
                                        ? row.yearsExp === 0
                                          ? ' · rookie'
                                          : ` · ${row.yearsExp} season${row.yearsExp === 1 ? '' : 's'} in`
                                        : ''
                                    }`
                              }
                            >
                              {row.age != null ? row.age.toFixed(1) : '\u2014'}
                            </td>
                            <td
                              className={`draft-table__num draft-table__offense ${
                                row.teamOffenseRank == null
                                  ? ''
                                  : row.teamOffenseRank <= OFFENSE_STRONG
                                    ? 'is-strong'
                                    : row.teamOffenseRank >= OFFENSE_WEAK
                                      ? 'is-weak'
                                      : ''
                              }`}
                              title={formatOffenseTooltip(row, priorSeason)}
                            >
                              {row.teamOffenseRank ?? <span className="draft-table__muted">—</span>}
                            </td>
                            <td
                              className="draft-table__num draft-table__lastyear"
                              title={formatLastFinishTooltip(row, priorSeason)}
                            >
                              {formatLastFinish(row) || (
                                <span className="draft-table__muted">
                                  {row.yearsExp === 0 ? 'R' : '—'}
                                </span>
                              )}
                            </td>
                            {!isAuction && dollarCell}
                            {!syncEnabled && (
                              <td className="draft-table__actions">
                                <button
                                  type="button"
                                  className={`draft-table__watch ${watchlist.has(row.id) ? 'is-active' : ''}`}
                                  onClick={() => toggleWatch(row.id)}
                                  aria-label={
                                    watchlist.has(row.id)
                                      ? `Remove ${row.name} from watchlist`
                                      : `Watch ${row.name}`
                                  }
                                  title={
                                    watchlist.has(row.id) ? 'Remove from watchlist' : 'Add to watchlist'
                                  }
                                >
                                  ★
                                </button>
                                <button
                                  type="button"
                                  className="draft-table__draft"
                                  onClick={() => markDrafted(row.id)}
                                  disabled={syncEnabled}
                                  aria-label={`Mark ${row.name} as drafted by another team`}
                                  title={
                                    syncEnabled
                                      ? 'Live sync is updating picks automatically'
                                      : 'Someone else took this player'
                                  }
                                >
                                  Gone
                                </button>
                                <button
                                  type="button"
                                  className="draft-table__mine"
                                  onClick={() => markDrafted(row.id, { mine: true })}
                                  disabled={syncEnabled}
                                  aria-label={`Add ${row.name} to your roster`}
                                  title={
                                    syncEnabled
                                      ? 'Live sync is updating picks automatically'
                                      : 'You drafted this player'
                                  }
                                >
                                  Mine
                                </button>
                              </td>
                            )}
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {displayRows.length > (focusMode ? 250 : 120) && (
                  <p className="draft-tool__truncated">
                    Showing top {focusMode ? 250 : 120} of {displayRows.length} available. Narrow
                    with search or position filters.
                  </p>
                )}
                {!displayRows.length && !loadingBoard && (
                  <p className="draft-tool__empty">No players match your filters.</p>
                )}
              </div>
            )}
          </section>

          <section className="dashboard__panel draft-tool__log-panel">
            <header className="draft-tool__log-head">
              <h3 className="draft-tool__section-title">Recent picks</h3>
              {draftLog.length > 0 && (
                <button type="button" className="draft-tool__copy" onClick={copyDraftLog}>
                  Copy list
                </button>
              )}
            </header>
            {!draftLog.length ? (
              <p className="draft-tool__placeholder">
                {syncEnabled
                  ? `Waiting for ${draftPlatform === 'espn' ? 'ESPN' : 'Sleeper'} picks… they will appear here as the draft progresses.`
                  : 'Picks you mark appear here. Start live sync, or use Gone / Mine on the board.'}
              </p>
            ) : (
              <ol className="draft-tool__log">
                {draftLog.slice(0, 40).map((entry) => {
                  const isMine =
                    entry.source === 'sleeper' || entry.source === 'espn'
                      ? myRosterId != null && String(entry.rosterId) === String(myRosterId)
                      : Boolean(entry.mine);
                  return (
                    <li
                      key={`${entry.id}-${entry.pickNo ?? entry.at}`}
                      className={`draft-tool__log-item ${isMine ? 'is-mine' : ''}`}
                    >
                      <span className="draft-tool__log-name">
                        {isAuction && entry.amount != null ? (
                          <span
                            className="draft-tool__log-pick"
                            title={(() => {
                              const ours = boardById.get(entry.id)?.auction;
                              if (ours == null) return `Sold for $${entry.amount}`;
                              const over = entry.amount - ours;
                              if (over === 0) return `Sold for $${entry.amount} — exactly this board's price`;
                              return over > 0
                                ? `Sold for $${entry.amount}, which is $${over} over this board's $${ours}`
                                : `Sold for $${entry.amount}, which is $${-over} under this board's $${ours}`;
                            })()}
                          >
                            {`$${entry.amount}`}{' '}
                          </span>
                        ) : entry.pickNo != null && !isAuction ? (
                          <span className="draft-tool__log-pick">
                            {draftInfo?.teams
                              ? formatPickSlot(entry.pickNo, draftInfo.teams)
                              : `#${entry.pickNo}`}{' '}
                          </span>
                        ) : null}
                        <BoardAvatar row={entry} map={headshotMap} size="xs" />
                        <span className="ff-avatar-name__label">{entry.name}</span>
                      </span>
                      <span className="draft-tool__log-meta">
                        {isMine ? 'You · ' : entry.teamName ? `${entry.teamName} · ` : ''}
                        {entry.position}
                        {entry.value != null ? ` · ${Math.round(entry.value)} VOR` : ''}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
            <p className="draft-tool__credit">
              Rankings ©{' '}
              <a href="https://keeptradecut.com/fantasy-rankings" target="_blank" rel="noreferrer">
                KeepTradeCut
              </a>
              . Projections, bye weeks, and site ranks ©{' '}
              <a href="https://firstseedsports.com/juicesheets/" target="_blank" rel="noreferrer">
                JuiceSheets
              </a>
              . Sleeper draft data © Sleeper.
            </p>
          </section>
        </div>
      </div>
    </article>
  );
};

export default DraftTool;
